/**
 * Donation Type Service
 *
 * Handles API calls for donation types data
 */

import { getApiClient } from './client';
import type {
  DonationType,
  DonationTypeListResponse,
  DonationTypeResponse,
} from '@/types/donation.types';

/**
 * Type guard to validate DonationType structure
 */
function isDonationType(obj: unknown): obj is DonationType {
  if (typeof obj !== 'object' || obj === null) return false;

  const dt = obj as Record<string, unknown>;

  return typeof dt.companyId === 'number' && typeof dt.donationType === 'number';
}

/**
 * Type guard to validate DonationTypeListResponse structure
 */
function isDonationTypeListResponse(obj: unknown): obj is DonationTypeListResponse {
  if (typeof obj !== 'object' || obj === null) return false;

  const response = obj as Record<string, unknown>;

  return (
    Array.isArray(response.items) &&
    response.items.every(isDonationType) &&
    typeof response.count === 'number'
  );
}

/**
 * Type guard to validate DonationTypeResponse structure
 */
function isDonationTypeResponse(obj: unknown): obj is DonationTypeResponse {
  if (typeof obj !== 'object' || obj === null) return false;

  const response = obj as Record<string, unknown>;

  return (
    typeof response.item === 'object' && response.item !== null && isDonationType(response.item)
  );
}

/**
 * Options for fetching donation types
 */
export interface GetDonationTypesOptions {
  statusCode?: number;
  taxReceipt?: number;
  showWeb?: number;
  showPledge?: number;
  showKiosk?: number;
  showTablet?: number;
  showPhone?: number;
}

/**
 * Fetch donation types for a specific company (web-enabled only)
 *
 * @param companyId - The company ID to fetch donation types for
 * @param options - Optional query filters
 * @returns Promise with donation types list
 * @throws Error if request fails or response is invalid
 */
export async function getDonationTypes(
  companyId: string | number,
  options: GetDonationTypesOptions = { statusCode: 2, taxReceipt: 1, showWeb: 1 }
): Promise<DonationTypeListResponse> {
  try {
    const apiClient = getApiClient();

    const params = new URLSearchParams();
    if (options.statusCode !== undefined) params.set('statusCode', String(options.statusCode));
    if (options.taxReceipt !== undefined) params.set('taxReceipt', String(options.taxReceipt));
    if (options.showWeb !== undefined) params.set('showWeb', String(options.showWeb));
    if (options.showPledge !== undefined) params.set('showPledge', String(options.showPledge));
    if (options.showKiosk !== undefined) params.set('showKiosk', String(options.showKiosk));
    if (options.showTablet !== undefined) params.set('showTablet', String(options.showTablet));
    if (options.showPhone !== undefined) params.set('showPhone', String(options.showPhone));

    const response = await apiClient.get<DonationTypeListResponse>(
      `/donationtypes/${companyId}?${params.toString()}`
    );

    // Validate response structure
    if (!isDonationTypeListResponse(response.data)) {
      throw new Error('Invalid response structure from donation types API');
    }

    return response.data;
  } catch (error) {
    // Re-throw with additional context
    if (error instanceof Error) {
      throw new Error(`Failed to fetch donation types: ${error.message}`);
    }
    throw error;
  }
}

/**
 * Fetch a single donation type by ID
 *
 * @param companyId - The company ID
 * @param donationTypeId - The donation type ID
 * @returns Promise with donation type details
 * @throws Error if request fails or response is invalid
 */
export async function getDonationType(
  companyId: string | number,
  donationTypeId: number
): Promise<DonationType> {
  try {
    const apiClient = getApiClient();
    const response = await apiClient.get<DonationTypeResponse>(
      `/donationtypes/${companyId}/${donationTypeId}`
    );

    if (!isDonationTypeResponse(response.data)) {
      throw new Error('Invalid response structure from donation type API');
    }

    return response.data.item;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch donation type: ${error.message}`);
    }
    throw error;
  }
}

/**
 * Parse suggested amounts from CSV string
 *
 * @param suggestedAmounts - CSV string of amounts
 * @returns Array of numbers
 */
export function parseSuggestedAmounts(suggestedAmounts: string | null): number[] {
  if (!suggestedAmounts) return [];

  return suggestedAmounts
    .split(',')
    .map((amount) => parseFloat(amount.trim()))
    .filter((amount) => !isNaN(amount) && amount > 0);
}
